import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { adminApi } from '../../services/adminApi';
import {
  BookOpen,
  Search,
  Check,
  X,
  Clock,
  Zap,
  Sparkles
} from 'lucide-react';
import { Button } from '../ui/Button';
import { LoadingSpinner } from '../ui/LoadingSpinner';

interface Word {
  id: string;
  word: string;
  phonetic_transcription?: string;
  definition: string;
  example_sentence?: string;
  difficulty_level: number;
  category?: string;
  age_group: string;
  status: 'pending' | 'approved' | 'rejected';
  created_by: string;
  created_at: string;
  updated_at: string;
  generation_metadata?: any;
}

interface WordGenerationRequest {
  count: number;
  difficultyLevel: number;
  ageGroup: string;
  category?: string;
  customPrompt?: string;
}

interface WordStats {
  pending_count: number;
  approved_count: number;
  rejected_count: number;
  total_count: number;
}

const WordManagement: React.FC = () => {
  const [words, setWords] = useState<Word[]>([]);
  const [stats, setStats] = useState<WordStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedWords, setSelectedWords] = useState<string[]>([]);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showGenerationModal, setShowGenerationModal] = useState(false);
  const [generationRequest, setGenerationRequest] = useState<WordGenerationRequest>({
    count: 1,
    difficultyLevel: 3,
    ageGroup: '7-9',
    category: '',
    customPrompt: ''
  });

  useEffect(() => {
    loadWords();
    loadStats();
  }, [filter]);

  const loadWords = async () => {
    setLoading(true);
    try {
      const data = await adminApi.getPendingWords(1, 50, filter);
      setWords(data.words || []);
    } catch (error) {
      console.error('Failed to load words:', error);
    }
    setLoading(false);
  };

  const loadStats = async () => {
    try {
      const data = await adminApi.getWordStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const generateWords = async () => {
    setGenerating(true);
    try {
      const data = await adminApi.generateWords({
        count: generationRequest.count,
        difficultyLevel: generationRequest.difficultyLevel,
        ageGroup: generationRequest.ageGroup,
        category: generationRequest.category || undefined,
        customPrompt: generationRequest.customPrompt || undefined
      });

      if (data.success && data.generatedCount > 0) {
        alert(`Successfully generated ${data.generatedCount} words!`);
        setShowGenerationModal(false);
        loadWords();
        loadStats();
      } else {
        alert(data.message || 'No words were generated. Please check your permissions and try again.');
      }
    } catch (error) {
      console.error('Generation error:', error);
      alert(`Failed to generate words: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
    setGenerating(false);
  };

  const approveWords = async (wordIds: string[]) => {
    try {
      const data = await adminApi.approveWords(wordIds);
      alert(`Approved ${data.approved_count} words successfully!`);
      loadWords();
      loadStats();
      setSelectedWords([]);
    } catch (error) {
      console.error('Approval error:', error);
      alert('Failed to approve words');
    }
  };

  const rejectWords = async (wordIds: string[], reason: string) => {
    try {
      const data = await adminApi.rejectWords(wordIds, reason);
      alert(`Rejected ${data.rejected_count} words successfully!`);
      loadWords();
      loadStats();
      setSelectedWords([]);
    } catch (error) {
      console.error('Rejection error:', error);
      alert('Failed to reject words');
    }
  };

  const filteredWords = words.filter(word =>
    word.word.toLowerCase().includes(searchTerm.toLowerCase()) ||
    word.definition.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BookOpen className="w-8 h-8 text-purple-600" />
          <div>
            <h2 className="text-3xl font-bold text-gray-800">Word Management</h2>
            <p className="text-gray-600">Generate, review, and manage vocabulary words</p>
          </div>
        </div>
        <Button
          onClick={() => setShowGenerationModal(true)}
          className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
        >
          <Sparkles className="w-5 h-5 mr-2" />
          Generate Words
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Words"
          value={stats?.total_count || 0}
          icon={<BookOpen />}
          color="text-blue-600"
          bgColor="bg-blue-50"
        />
        <StatCard
          title="Pending Review"
          value={stats?.pending_count || 0}
          icon={<Clock />}
          color="text-yellow-600"
          bgColor="bg-yellow-50"
        />
        <StatCard
          title="Approved"
          value={stats?.approved_count || 0}
          icon={<Check />}
          color="text-green-600"
          bgColor="bg-green-50"
        />
        <StatCard
          title="Rejected"
          value={stats?.rejected_count || 0}
          icon={<X />}
          color="text-red-600"
          bgColor="bg-red-50"
        />
      </div>

      {/* Filters and Actions */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search words..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Words</option>
            <option value="pending">Pending Review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        {selectedWords.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">{selectedWords.length} selected</span>
            <Button
              onClick={() => approveWords(selectedWords)}
              className="bg-green-500 hover:bg-green-600"
            >
              <Check className="w-4 h-4 mr-1" />
              Approve
            </Button>
            <Button
              onClick={() => rejectWords(selectedWords, 'Bulk rejection')}
              className="bg-red-500 hover:bg-red-600"
            >
              <X className="w-4 h-4 mr-1" />
              Reject
            </Button>
          </div>
        )}
      </div>

      {/* Words Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={selectedWords.length === filteredWords.length && filteredWords.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedWords(filteredWords.map(w => w.id));
                      } else {
                        setSelectedWords([]);
                      }
                    }}
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Word</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Definition</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Difficulty</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center">
                    <LoadingSpinner />
                  </td>
                </tr>
              ) : filteredWords.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                    No words found
                  </td>
                </tr>
              ) : (
                filteredWords.map((word) => (
                  <motion.tr
                    key={word.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedWords.includes(word.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedWords([...selectedWords, word.id]);
                          } else {
                            setSelectedWords(selectedWords.filter(id => id !== word.id));
                          }
                        }}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{word.word}</div>
                        {word.phonetic_transcription && (
                          <div className="text-sm text-gray-500">{word.phonetic_transcription}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        {word.definition}
                      </div>
                      {word.example_sentence && (
                        <div className="text-sm text-gray-500 max-w-xs truncate italic">
                          "{word.example_sentence}"
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {word.category || 'General'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        word.difficulty_level <= 2 ? 'bg-green-100 text-green-800' :
                        word.difficulty_level <= 4 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        Level {word.difficulty_level}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        word.status === 'approved' ? 'bg-green-100 text-green-800' :
                        word.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {word.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        {word.status === 'pending' && (
                          <>
                            <button
                              onClick={() => approveWords([word.id])}
                              className="text-green-600 hover:text-green-900"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => rejectWords([word.id], 'Not suitable')}
                              className="text-red-600 hover:text-red-900"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Word Generation Modal */}
      <AnimatePresence>
        {showGenerationModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
            >
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                Generate Words
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Number of Words
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={generationRequest.count}
                    onChange={(e) => setGenerationRequest({
                      ...generationRequest,
                      count: parseInt(e.target.value) || 1
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Difficulty Level
                  </label>
                  <select
                    value={generationRequest.difficultyLevel}
                    onChange={(e) => setGenerationRequest({
                      ...generationRequest,
                      difficultyLevel: parseInt(e.target.value)
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value={1}>Level 1 - Very Easy</option>
                    <option value={2}>Level 2 - Easy</option>
                    <option value={3}>Level 3 - Medium</option>
                    <option value={4}>Level 4 - Hard</option>
                    <option value={5}>Level 5 - Very Hard</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Age Group
                  </label>
                  <select
                    value={generationRequest.ageGroup}
                    onChange={(e) => setGenerationRequest({
                      ...generationRequest,
                      ageGroup: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="4-6">4-6 years</option>
                    <option value="7-9">7-9 years</option>
                    <option value="10-12">10-12 years</option>
                    <option value="13-16">13-16 years</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., animals, colors, emotions..."
                    value={generationRequest.category || ''}
                    onChange={(e) => setGenerationRequest({
                      ...generationRequest,
                      category: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Custom Prompt (Optional)
                  </label>
                  <textarea
                    placeholder="Additional requirements for word generation..."
                    value={generationRequest.customPrompt || ''}
                    onChange={(e) => setGenerationRequest({
                      ...generationRequest,
                      customPrompt: e.target.value
                    })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <Button
                  onClick={() => setShowGenerationModal(false)}
                  variant="secondary"
                >
                  Cancel
                </Button>
                <Button
                  onClick={generateWords}
                  disabled={generating}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                >
                  {generating ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4 mr-2" />
                      Generate
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const StatCard = ({ title, value, icon, color, bgColor }: {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
}) => (
  <motion.div
    whileHover={{ scale: 1.05 }}
    className={`p-4 rounded-lg ${bgColor} border border-gray-200`}
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-sm text-gray-600">{title}</p>
      </div>
      <div className={`${color}`}>
        {icon}
      </div>
    </div>
  </motion.div>
);

export default WordManagement;
